import * as api from '../api/mutations';
import {
  agentListQueryKey,
  brandListQueryKey,
  jobApplicationsQueryKey,
  jobListingsQueryKey,
  invoicesQueryKey,
} from '../api/queries';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { IAgent, IAgentBrand } from '../types/agent';
import { IBrand, IRequirement } from '../types/brand';
import { LoginInputs } from '../validations/login';
import { JobListing } from '../types/jobs';
import { AddClientResponse, clientListQueryKey } from '../api/client/addClient';
import { ClientSchemaType } from '../validations/client';
import { Invoice } from '../types/invoice';
import { ILeaveRequest, IUpdateLeaveRequestStatus } from '../types/leave';
import { updateLeaveRequestStatus } from '../api/leave-requests/updateLeaveRequestStatus';
import { getLeaveRequestsQueryKey } from '../api/leave-requests/getLeaveRequests';

export const postJobListing = (
  options?: Omit<
    UseMutationOptions<JobListing, Error, JobListing>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.postJobApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateJobListing = (
  options?: Omit<
    UseMutationOptions<api.UpdateJobListingResponse, Error, JobListing>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateJobListingApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: jobListingsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const deleteJobListing = (
  options?: Omit<
    UseMutationOptions<api.DeleteJobListingResponse, Error, JobListing>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.deleteJobListingApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: jobListingsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const addAgentBrand = (
  options?: Omit<
    UseMutationOptions<IAgentBrand, Error, IAgentBrand>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.addAgentBrandApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateAgentBrand = (
  options?: Omit<
    UseMutationOptions<IAgentBrand, Error, IAgentBrand>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateAgentBrandApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const addAgent = (
  options?: Omit<UseMutationOptions<IAgent, Error, IAgent>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.addAgentApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const addRequirement = (
  options?: Omit<
    UseMutationOptions<api.AddRequirementResponse, Error, IRequirement>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.addRequirementApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: api.requirementsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const deleteRequirement = (
  options?: Omit<
    UseMutationOptions<
      api.DeleteRequirementResponse,
      Error,
      api.DeleteRequirementPayload
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.deleteRequirement,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: api.requirementsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateRequirement = (
  options?: Omit<
    UseMutationOptions<
      api.UpdateRequirementResponse,
      Error,
      { _id: string; status: 'incomplete' | 'complete' }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateRequirementApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: api.requirementsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const addBrand = (
  options?: Omit<
    UseMutationOptions<api.AddBrandResponse, Error, IBrand>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.addBrandApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: brandListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const addClient = (
  options?: Omit<
    UseMutationOptions<AddClientResponse, Error, ClientSchemaType>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.addClient,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: clientListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateClient = (
  options?: Omit<
    UseMutationOptions<api.UpdateClientResponse, Error, ClientSchemaType>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateClient,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: clientListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateAgent = (
  options?: Omit<
    UseMutationOptions<api.UpdateAgentResponse, Error, IAgent>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateAgentApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateBrand = (
  options?: Omit<
    UseMutationOptions<api.UpdateBrandResponse, Error, IBrand>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateBrandApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: brandListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateCandidateStatus = (
  options?: Omit<
    UseMutationOptions<api.UpdateCandidateStatusResponse, Error, any>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateCandidateStatusApi,
    onSuccess: (data, variables, context) => {
      // queryClient.invalidateQueries({ queryKey: jobApplicationsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const changeAgentPassword = (
  options?: Omit<
    UseMutationOptions<api.UpdateAgentPasswordResponse, Error, any>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateAgentPassword,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: agentListQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateJobApplication = (
  options?: Omit<
    UseMutationOptions<api.UpdateJobApplicationResponse, Error, any>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.updateJobApplicationApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: jobApplicationsQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context);
    },
  });
};

export const updateAgentWorklog = (
  options?: Omit<
    UseMutationOptions<
      api.UpdateAgentWorkLogResponse,
      Error,
      api.UpdateAgentWorklogPayload
    >,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: api.updateAgentWorklogApi,
  });

export const login = (
  options?: Omit<UseMutationOptions<any, Error, any>, 'mutationFn'>
) =>
  useMutation({
    ...options,
    mutationFn: (data: LoginInputs) => api.loginApi(data.email, data.password),
  });

export const generateInvoice = (
  options?: Omit<
    UseMutationOptions<
      api.GenerateInvoiceResponse,
      Error,
      api.GenerateInvoicePayload
    >,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: api.generateInvoiceApi,
  });

export const ssoLogin = (
  options?: Omit<
    UseMutationOptions<
      any,
      Error,
      { ssoToken: string }
    >,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: (data: { ssoToken: string }) => api.ssoLoginApi(data.ssoToken),
  });

export const sendOnboardingForm = (
  options?: Omit<
    UseMutationOptions<
      api.SendOnboardingFormResponse,
      Error,
      api.SendOnboardingFormPayload
    >,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: (data: api.SendOnboardingFormPayload) =>
      api.sendOnboardingForm(data),
  });

export const updateMismatchRequestStatus = (
  options?: Omit<
    UseMutationOptions<
      api.UpdateMismatchRequestStatusResponse,
      Error,
      api.UpdateMismatchRequestStatusPayload
    >,
    'mutationFn'
  >
) => useMutation({ ...options, mutationFn: api.updateMismatchRequestStatus });

export const approveMismatchRequest = (
  options?: Omit<
    UseMutationOptions<
      api.ApproveMismatchRequestResponse,
      Error,
      api.ApproveMismatchRequestPayload
    >,
    'mutationFn'
  >
) => useMutation({ ...options, mutationFn: api.approveMismatchRequest });

export const deleteAgentLog = (
  options?: Omit<
    UseMutationOptions<
      api.DeleteAgentLogResponse,
      Error,
      api.DeleteAgentLogPayload
    >,
    'mutationFn'
  >
) => useMutation({ ...options, mutationFn: api.deleteAgentLog });

export const addAgentLog = (
  options?: Omit<
    UseMutationOptions<
      api.AddAgentWorkLogResponse,
      Error,
      api.AddAgentWorklogPayload
    >,
    'mutationFn'
  >
) => useMutation({ ...options, mutationFn: api.addAgentWorklogApi });

export const createInvoice = (
  options?: Omit<
    UseMutationOptions<api.CreateInvoiceResponse, Error, any>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: api.createInvoiceApi,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const updateInvoice = (
  invoiceId: string,
  options?: Omit<
    UseMutationOptions<api.UpdateInvoiceResponse, Error, Invoice>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (updatedInvoice: Invoice) =>
      api.updateInvoiceApi(invoiceId, updatedInvoice),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const uploadPdf = (
  options?: Omit<
    UseMutationOptions<api.UploadPdfResponse, Error, { id: string; pdfs: any }>,
    'mutationFn'
  >
) =>
  useMutation({
    ...options,
    mutationFn: ({ id, pdfs }: { id: string; pdfs: any }) =>
      api.uploadPdfApi(id, pdfs),
  });

export const useUpdateLeaveRequestStatus = (
  options?: Omit<
    UseMutationOptions<
      { success: boolean; data?: { leaveRequest: ILeaveRequest }; message?: string },
      Error,
      IUpdateLeaveRequestStatus
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateLeaveRequestStatus,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: getLeaveRequestsQueryKey() });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
